<template>
  <BaseLoader
    is-block
    :is-active="isChartLoading || modelValue.isSaving"
    :class="$style.wrapper"
  >
    <component
      :is="modelValue.type === 'number' ? 'BaseNumberChart' : 'VolumesChart'"
      ref="chart"
      :labels="labels"
      :datasets="datasets"
      :aspect-ratio="ratio"
      :type="modelValue.type"
      :custom-options="customChartOptions"
      has-cagr
      @showActions="updateLocalModel"
      @hideActions="saveModel"
    >
      <template #heading>
        {{ heading }}
      </template>

      <template #actions>
        <div :class="$style.actionsWrapper">
          <BaseDropdownHeading>
            Edit this chart
          </BaseDropdownHeading>

          <BaseDropdownActionsList>
            <BaseDropdownActionField>
              <template #label>
                CHART NAME
              </template>

              <template #field>
                <BaseInput
                  v-model="localModel.name"
                  placeholder="Unnamed chart"
                />
              </template>
            </BaseDropdownActionField>

            <BaseDropdownActionField>
              <template #label>
                DATA INPUT
              </template>

              <template #field>
                <BaseMultiselect
                  v-model="localModel.source"
                  :options="$options.SOURCE_OPTIONS"
                  :class="$style.multiselect"
                  is-block
                />
              </template>
            </BaseDropdownActionField>

            <BaseDropdownActionField v-if="isLoadingChartSchema">
              <template #field>
                <BaseLoader
                  is-block
                  is-active
                />
              </template>
            </BaseDropdownActionField>

            <template v-if="chartSchema && !isLoadingChartSchema">
              <BaseDropdownActionField
                v-if="chartSchema.details?.length > 0 && localModel.type !== $options.CHARTS_TOOLS.TYPE.number"
              >
                <template #label>
                  Lines To Display
                </template>

                <template #field>
                  <BaseMultiselect
                    v-model="localModel.parameters.details"
                    :options="chartSchema.details.filter((item) => {return !['Complexity'].includes(item.label)})"
                    :class="$style.multiselect"
                    is-block
                  />
                </template>
              </BaseDropdownActionField>

              <div :class="$style.filterSection">
                <span :class="$style.filterSectionHeading">
                  Filter By
                </span>
                <div
                  :class="$style.filterSectionBody"
                  :style="{ paddingRight: `${20 - $options.SCROLLBAR_WIDTH}px` }"
                >
                  <span>
                    <BaseButton
                      variant="secondary"
                      :class="$style.actionButton"
                      @click="resetAll"
                    >
                      Reset all
                    </BaseButton>
                  </span>
                  <template
                    v-for="item in chartSchema.filter"
                    :key="item.name"
                  >
                    <BaseDropdownActionField v-if="!['complexity'].includes(item.name)">
                      <template #label>
                        {{ item.label }}
                      </template>

                      <template #field>
                        <BaseDateInput
                          v-if="item.type === 'dateRange'"
                          v-model="localModel.parameters.filter[item.name]"
                          :class="$style.dateInput"
                          is-range
                        />
                        <BaseMultiselect
                          v-else-if="item.type === 'select'"
                          v-model="localModel.parameters.filter[item.name]"
                          :options="chartSchemaOptions[item.name] ? chartSchemaOptions[item.name] : [{value:''}]"
                          :class="$style.multiselect"
                          is-searchable
                          is-block
                        />
                      </template>
                    </BaseDropdownActionField>
                  </template>
                </div>
              </div>
            </template>

            <BaseDropdownActionField>
              <template #label>
                DISPLAY AS
              </template>

              <template #field>
                <div :class="$style.typeSelector">
                  <button
                    v-for="type in $options.CHART_TYPES"
                    :key="type"
                    :class="[
                      $style.typeButton,
                      {
                        [$style.isActive]: localModel.type === type,
                      },
                    ]"
                    type="button"
                    @click="setType(type)"
                  >
                    <ChartTypeBarSvg v-if="type === 'bar'" />
                    <ChartTypeLineSvg v-else-if="type === 'line'" />
                    <ChartTypeNumberSvg v-else-if="type === 'number'" />
                  </button>
                  <BaseLabeledCheckbox
                    v-for="option in $options.STACKED_GRAPH"
                    :key="option.value"
                    v-model="localModel.stacked"
                    :value="option.value"
                    :class="$style.stackedCheckBox"
                    is-dark
                    has-background-highlight
                  >
                    {{ option.label }}
                  </BaseLabeledCheckbox>
                </div>
              </template>
            </BaseDropdownActionField>
          </BaseDropdownActionsList>
          <div :class="$style.button">
            <BaseButton
              variant="success"
              @click="showData"
            >
              Show graph
            </BaseButton>
          </div>
          <div :class="$style.buttons">
            <BaseButton
              @click="move"
            >
              Move
            </BaseButton>
            <BaseButton
              variant="danger"
              @click="remove"
            >
              Remove
            </BaseButton>
          </div>
        </div>
      </template>
    </component>
  </BaseLoader>
</template>

<script>
import { mapState } from 'vuex';
import { cloneDeep, difference } from 'lodash';
import BaseNumberChart from '@/components/base/NumberChart';
import {
  DropdownHeading as BaseDropdownHeading, DropdownActionsList as BaseDropdownActionsList,
  DropdownActionField as BaseDropdownActionField,
} from '@/components/base/Dropdown';
import BaseLabeledCheckbox from '@/components/base/LabeledCheckbox';
import BaseInput from '@/components/base/Input';
import BaseButton from '@/components/base/Button';
import BaseMultiselect from '@/components/base/Multiselect';
import BaseLoader from '@/components/base/Loader';
import BaseDateInput from '@/components/base/DateInput';
import VolumesChart from '@/components/VolumesChart';
import * as chartsTools from '@/charts';
import ChartTypeBarSvg from '@/assets/images/icons/chart-type-bar.svg?inline';
import ChartTypeLineSvg from '@/assets/images/icons/chart-type-line.svg?inline';
import ChartTypeNumberSvg from '@/assets/images/icons/chart-type-number.svg?inline';

function getScrollbarWidth() {
  // Creating invisible container
  const outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll'; // forcing scrollbar to appear
  outer.style.msOverflowStyle = 'scrollbar'; // needed for WinJS apps
  document.body.appendChild(outer);

  // Creating inner element and placing it in the container
  const inner = document.createElement('div');
  outer.appendChild(inner);

  // Calculating difference between container's full width and the child width
  const scrollbarWidth = (outer.offsetWidth - inner.offsetWidth);

  // Removing temporary elements from the DOM
  outer.parentNode.removeChild(outer);

  return scrollbarWidth;
}

export default {
  components: {
    BaseNumberChart,
    BaseDropdownHeading,
    BaseDropdownActionsList,
    BaseDropdownActionField,
    BaseLabeledCheckbox,
    BaseInput,
    BaseButton,
    BaseMultiselect,
    BaseLoader,
    BaseDateInput,
    VolumesChart,
    ChartTypeBarSvg,
    ChartTypeLineSvg,
    ChartTypeNumberSvg,
  },
  props: {
    modelValue: {
      type: Object,
      default: null,
      required: true,
    },
    ratio: {
      type: Number,
      default: 2,
    },
  },
  emits: {
    'update:modelValue': null,
    remove: null,
    move: null,
  },
  SCROLLBAR_WIDTH: getScrollbarWidth(),
  CHART_TYPES: Object.values(chartsTools.TYPE),
  CHARTS_TOOLS: chartsTools,
  SOURCE_OPTIONS: chartsTools.sources.map((source) => ({
    label: source.label,
    value: source.name,
  })),
  STACKED_GRAPH: [{ label: 'Stacked', value: true }],
  data() {
    return {
      labels: [],
      datasets: [],
      localModel: null,
      chartSchema: null,
      chartSchemaOptions: null,
      isLoadingChartSchema: false,
      isChartSchemaLoaded: false,
      isChartLoading: false,
      isRemoving: false,
      prevValue: null,
      isStacked: [],
      customChartOptions: null,
    };
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapState('app', ['updateReferenceId']),
    heading() {
      return this.modelValue.name ?? 'Unnamed chart';
    },
    sourceTools() {
      return chartsTools.sourcesMap[this.localModel.source];
    },
  },
  watch: {
    modelValue() {
      this.updateLocalModel();
    },
    'localModel.source': function sourceChange(val) {
      if (this.modelValue.source === val) return;
      this.loadChartSchema();
    },
    'localModel.parameters.filter': {
      deep: true,
      handler: function modelFilterChanged(newValue) {
        const { prevValue } = this;
        const changedField = { field: null, direction: null };
        if (newValue && prevValue) {
          Object.keys(newValue).filter((item) => {
            if (difference(prevValue[item], newValue[item]).length !== 0) {
              changedField.field = item;
              changedField.direction = 'remove';
            }

            if (difference(newValue[item], prevValue[item]).length !== 0) {
              changedField.field = item;
              changedField.direction = 'add';
            }

            return true;
          });
          this.prevValue = cloneDeep(newValue);
        }
        if (changedField.field) {
          this.loadDynamicSchemaOptions(changedField);
        }
      },
    },
  },
  async created() {
    this.isChartLoading = true;
    this.updateLocalModel();
    await this.loadChartSchema(true);
    await this.showData();
    if (this.localModel.parameters) {
      this.prevValue = cloneDeep(this.localModel.parameters.filter);
    }
  },
  methods: {
    updateLocalModel() {
      this.localModel = cloneDeep(this.modelValue);
    },
    setType(type) {
      this.localModel.type = type;
    },
    async loadChartSchema(isInitial = false) {
      this.isLoadingChartSchema = true;
      this.chartSchema = null;
      this.chartSchemaOptions = null;
      if (!isInitial) {
        this.localModel.parameters = null;
      }
      if (this.sourceTools) {
        this.chartSchema = this.sourceTools.getSchema();
        this.chartSchemaOptions = await this.sourceTools.getOptions();
        if (!isInitial) {
          this.localModel.parameters = this.sourceTools.getModel();
        }
      }
      this.isLoadingChartSchema = false;
      this.isChartSchemaLoaded = true;
    },
    async showData() {
      this.isChartLoading = true;
      if (this.localModel.source == null || !this.isChartSchemaLoaded) {
        this.isChartLoading = false;
        return;
      }

      const data = await this.sourceTools.getData(this.user.company.uuid, this.updateReferenceId);
      const filteredData = this.sourceTools.applyFilter(data, this.localModel.parameters.filter);
      const chart = this.sourceTools.getCharts(
        filteredData,
        this.localModel.parameters.details,
        null,
        this.localModel.stacked,
      )[0];

      this.labels = chart.labels;
      this.datasets = chart.datasets;
      this.customChartOptions = chart.options;
      this.isChartLoading = false;
    },
    openActions() {
      this.$refs.chart.$refs.chart.$refs.actionsButton.scrollIntoView();
      this.$refs.chart.$refs.chart.showActions();
    },
    saveModel() {
      if (this.isRemoving) return;
      this.$emit('update:modelValue', cloneDeep(this.localModel));
    },
    remove() {
      this.isRemoving = true;
      this.$refs.chart.$refs.chart.hideActions();
      this.$emit('remove');
    },
    move() {
      this.$refs.chart.$refs.chart.hideActions();
      this.$emit('move');
    },
    async loadDynamicSchemaOptions(changedField) {
      if (!this.isDataInitialized) {
        const data = await this.sourceTools.getData(this.user.company.uuid, this.updateReferenceId);
        if (data.length > 0) {
          this.chartSchemaOptions = {
            ...this.chartSchemaOptions,
            ...(await this.sourceTools.getDynamicOptions(this.localModel.parameters.filter, data, changedField)),
          };
        }
      }
    },
    async resetAll() {
      this.chartSchema = this.sourceTools.getSchema();
      this.chartSchemaOptions = await this.sourceTools.getOptions();
      this.localModel.parameters = this.sourceTools.getModel();
    },
  },
};
</script>

<style lang="scss" module>
.wrapper {
  display: flex;
  align-items: stretch;
  padding: 15px 20px;

  > * {
    flex-grow: 1;
    flex-shrink: 1;
    min-width: 0;
  }
}

.actionsWrapper {
  min-width: 300px;
}

.multiselect,
.dateInput {
  width: 260px;
}

.filterSection {
  margin-top: 15px !important;
  margin-right: -10px;
  margin-left: -10px;
}

.filterSectionHeading {
  display: inline-block;
  padding: 3px 5px 0;
  margin-left: 15px;
  font-size: 12px;
  font-weight: 500;
  color: $text;
  text-transform: uppercase;
  background-color: $background-light;
  border-radius: 5px 5px 0 0;
  opacity: 0.75;
}

.filterSectionBody {
  max-height: 240px;
  padding: 15px 20px;
  overflow-y: scroll;
  background-color: rgba($background-light, 0.75);

  > * {
    padding: 0;

    &:not(:first-child) {
      margin-top: 15px;
    }
  }
}

.typeSelector {
  display: flex;

  .typeButton:not(:first-child) {
    margin-left: 6px;
  }
}

.typeButton {
  @include reset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  font-size: 8px;
  color: $text;
  cursor: pointer;
  background: $background-light;
  border-radius: 5px;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background: rgba($disabled, 0.35);
  }

  &.isActive {
    color: #16ba62;
    background: #c1ffde;
  }
}

.buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 15px;
  padding: 0 20px;
  margin-top: 15px;
}

.button {
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 15px;
  padding: 0 20px;
  margin-top: 15px;
}

.actionButton {
  padding-top: 5px;
  padding-bottom: 5px;
  margin-top: 10px;
  margin-right: 10px;
}

.stackedCheckBox {
  margin-left: 8px;
}
</style>
